import { message } from 'antd'
import dayjs from 'dayjs'
import {
  getHealthPlanActionEntry,
  getHealthPlanEntry,
  useRolodexEdit,
} from 'features/fde'
import { useGeckoPermissions } from 'features/permissions'
import { useCallback, useEffect, useState } from 'react'
import {
  GeckoAssetHealthAction,
  GeckoAssetHealthPlan,
  GeckoComponent,
  GeckoSite,
  GeckoUnit,
  PERMISSION_CANTILEVER_HEALTH_PLAN_EDITOR,
  PERMISSION_CANTILEVER_HEALTH_PLAN_VIEWER,
  PERMISSION_GECKO_INTERNAL_MEMBER,
  RolodexEntryType,
} from 'types'

interface UseHealthPlanningWidgetProps {
  unit: GeckoUnit | undefined
  site: GeckoSite | undefined
  onPlanCreated?: (planId: string) => void
  onActionCreated?: (actionId: string) => void
  onSelectedPlanChange?: (plan: GeckoAssetHealthPlan | null) => void
  healthPlans: readonly GeckoAssetHealthPlan[]
  planToActions: Map<string, GeckoAssetHealthAction[]>
  allActions: readonly GeckoAssetHealthAction[]
  components: readonly GeckoComponent[]
  forceRefresh: () => Promise<void>
}

export const useHealthPlanningWidget = ({
  unit,
  site,
  onPlanCreated,
  onActionCreated,
  onSelectedPlanChange,
  healthPlans,
  allActions,
  components,
  forceRefresh,
}: UseHealthPlanningWidgetProps) => {
  const [selectedPlanId, setSelectedPlanId] = useState<string | undefined>()

  const { permissions } = useGeckoPermissions()
  const { rolodexPut, rolodexPatch } = useRolodexEdit()

  const canEdit =
    permissions?.has(PERMISSION_CANTILEVER_HEALTH_PLAN_EDITOR) ?? false
  const canView =
    permissions?.has(PERMISSION_CANTILEVER_HEALTH_PLAN_VIEWER) ||
    permissions?.has(PERMISSION_CANTILEVER_HEALTH_PLAN_EDITOR) ||
    permissions?.has(PERMISSION_GECKO_INTERNAL_MEMBER) ||
    false

  useEffect(() => {
    const preferredPlan = healthPlans.find((plan) => plan.data.preferred)
    onSelectedPlanChange?.(preferredPlan || null)
  }, [healthPlans, onSelectedPlanChange])

  const createHealthPlan = useCallback(
    async (planName: string) => {
      if (!canEdit) {
        message.error('You do not have permission to create mitigating actions')
        return
      }
      if (!unit || !site) {
        message.error('Unit and site are required')
        return
      }

      try {
        const healthPlanEntry = getHealthPlanEntry({
          name: planName,
          preferred: healthPlans.length === 0, // Make first plan preferred
          site,
        })

        // Add unit tag to the health plan
        healthPlanEntry.tags = {
          ...healthPlanEntry.tags,
          [RolodexEntryType.UNIT]: unit.id,
        }

        await rolodexPut([healthPlanEntry])
        await forceRefresh()

        onPlanCreated?.(healthPlanEntry.id)
      } catch (error) {
        console.error('Failed to create health plan:', error)
        message.error('Failed to create health plan')
      }
    },
    [
      canEdit,
      site,
      healthPlans.length,
      rolodexPut,
      forceRefresh,
      onPlanCreated,
      unit,
    ]
  )

  const createAction = useCallback(
    async (
      planId: string,
      actionData: Partial<GeckoAssetHealthAction['data']> & {
        componentId?: string
      }
    ) => {
      if (!canEdit) {
        message.error('You do not have permission to create actions')
        return
      }
      if (!unit || !site) {
        message.error('Unit and site are required')
        return
      }

      try {
        const plan = healthPlans.find((p) => p.id === planId)
        if (!plan) {
          throw new Error('Health plan not found')
        }

        // Get selected component if componentId is provided
        const selectedComponent = actionData.componentId
          ? components.find((c) => c.id === actionData.componentId)
          : undefined

        const actionEntry = getHealthPlanActionEntry({
          name: actionData.name || 'New Action',
          date: actionData.actionDate
            ? dayjs(actionData.actionDate)
            : dayjs().add(1, 'month'),
          plan,
          actionType: actionData.actionType || 'general',
          site,
          selectedUnit: unit,
          selectedComponent,
          actionCost: actionData.actionCost,
          costTypeHierarchy: actionData.costTypeHierarchy,
          actionTags: actionData.actionTags,
          deficiencyIds: actionData.deficiencyIds,
        })

        await rolodexPut([actionEntry])
        await forceRefresh()

        onActionCreated?.(actionEntry.id)
      } catch (error) {
        console.error('Failed to create action:', error)
        message.error('Failed to create action')
      }
    },
    [
      canEdit,
      healthPlans,
      unit,
      components,
      site,
      rolodexPut,
      forceRefresh,
      onActionCreated,
    ]
  )

  const editHealthPlan = useCallback(
    async (planId: string, updates: Partial<GeckoAssetHealthPlan['data']>) => {
      if (!canEdit) {
        message.error('You do not have permission to edit mitigating actions')
        return
      }

      try {
        await rolodexPatch([{ id: planId, data: updates }])
        await forceRefresh()
        message.success('Health plan updated successfully')
      } catch (error) {
        console.error('Failed to edit health plan:', error)
        message.error('Failed to edit health plan')
      }
    },
    [canEdit, rolodexPatch, forceRefresh]
  )

  const editAction = useCallback(
    async (
      actionId: string,
      updates: Partial<GeckoAssetHealthAction['data']> & {
        componentId?: string
      }
    ) => {
      if (!canEdit) {
        message.error('You do not have permission to edit actions')
        return
      }

      try {
        // Handle component updates by updating the action's tags
        if ('componentId' in updates) {
          const { componentId, ...actionUpdates } = updates

          // For component updates, we need to patch both data and tags
          const currentAction = allActions.find((a) => a.id === actionId)
          if (currentAction) {
            const updatedTags = {
              ...currentAction.tags,
              [RolodexEntryType.COMPONENT]: componentId || null,
            }

            await rolodexPatch([
              {
                id: actionId,
                data: actionUpdates,
                tags: updatedTags,
              },
            ])
          }
        } else {
          await rolodexPatch([{ id: actionId, data: updates }])
        }

        await forceRefresh()
        message.success('Action updated successfully')
      } catch (error) {
        console.error('Failed to edit action:', error)
        message.error('Failed to edit action')
      }
    },
    [canEdit, allActions, rolodexPatch, forceRefresh]
  )

  return {
    // Permissions
    canEdit,
    canView,

    // State
    selectedPlanId,
    setSelectedPlanId,

    // Actions
    createHealthPlan,
    createAction,
    editHealthPlan,
    editAction,
  }
}
